import { useEffect, useState } from 'react';
import { Choices } from '@/components/fields';
import { useAuthStore } from '@/stores/auth.store';
import { can } from '@/lib/session';
import { Link } from 'react-router-dom';
import type { AdminCartDto, SavedListKind, SavedProductSummaryDto } from '@ecom/contracts';
import { FilterBar, FilterField } from '@/components/filters';
import { CartIcon, ClockIcon, CoinsIcon, MailIcon, StarIcon } from '@/components/icons';
import { AmountRow, ListCard, ListToolbar, LoadMore, Page, Sheet, TableScroll, Table, Tabs, Td, Th, Thumb, Tr } from '@/components/page';
import { Alert, Button, EmptyState, MetricCard, Pill, Select, TableSkeleton } from '@/components/ui';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import { age, count, date, dateTime, money } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { EMPTY_CART_FILTERS, useShopperActivityStore, type CartFilters } from '@/stores/shopper-activity.store';

/**
 * What shoppers are holding (ADR-063) — every cart with something in it, an
 * account's or a guest's, and the products on wishlists and compare lists,
 * counted across the store.
 *
 * Carts read like the order page: lines with their pictures, valued at today's
 * retail prices by the server. A cart idle past the abandoned-cart delay says
 * so; it is a label, and a cart that moves again is open again. A saved list
 * is counted by product, most saved first; a product opens the accounts that
 * saved it — a guest is counted, never named.
 */

type View = 'carts' | 'WISHLIST' | 'COMPARE';

export function ShopperActivityPage() {
  const t = useT();
  const [view, setView] = useState<View>('carts');
  const { saved } = useShopperActivityStore();

  return (
    <Page title={t('shopperActivity')} subtitle={t('shopperActivitySubtitle')} fill>
      {view === 'carts' ? <CartCards /> : null}
      <ListCard>
        <Tabs
          className="px-2"
          label={t('shopperActivity')}
          value={view}
          onChange={setView}
          items={[
            { id: 'carts', label: t('cartsTab') },
            { id: 'WISHLIST', label: t('wishlistsTab'), count: saved.WISHLIST.total ?? undefined },
            { id: 'COMPARE', label: t('compareTab'), count: saved.COMPARE.total ?? undefined },
          ]}
        />
        {view === 'carts' ? <Carts /> : <SavedProducts key={view} kind={view} />}
      </ListCard>
    </Page>
  );
}

/** Open and abandoned carts, and what they hold. */
function CartCards() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { summary, loadSummary } = useShopperActivityStore();

  useEffect(() => {
    void loadSummary();
  }, [loadSummary]);

  if (!summary) return null;
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <MetricCard
        label={t('openCarts')}
        value={count(summary.open.carts, locale)}
        hint={t('cartsHolding', { amount: money(summary.open.value, locale, summary.currency) })}
        tone="sky"
        icon={<CartIcon />}
      />
      <MetricCard
        label={t('abandonedCarts')}
        value={count(summary.abandoned.carts, locale)}
        hint={t('abandonedAfter', { hours: count(summary.abandonedAfterHours, locale) })}
        tone="amber"
        icon={<ClockIcon />}
      />
      <MetricCard
        label={t('abandonedValue')}
        value={money(summary.abandoned.value, locale, summary.currency)}
        hint={t('atTodaysPrices')}
        tone="rose"
        icon={<CoinsIcon />}
      />
      <MetricCard
        label={t('recoveryMailsSent')}
        value={count(summary.recoveryMailed, locale)}
        hint={t('recoveryMailsHint')}
        tone="violet"
        icon={<MailIcon />}
      />
    </div>
  );
}

function Carts() {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { carts, cartsCursor, cartsTotal, cartsLoading, cartFilters, error, loadCarts, loadMoreCarts } =
    useShopperActivityStore();
  const [open, setOpen] = useState<AdminCartDto | null>(null);

  useEffect(() => {
    void loadCarts();
  }, [loadCarts, locale]);

  const chips = [
    ...(cartFilters.state
      ? [{ key: 'state', label: cartFilters.state === 'abandoned' ? t('abandoned') : t('cartOpen'), onRemove: () => void loadCarts({ state: '' }) }]
      : []),
    ...(cartFilters.who
      ? [{ key: 'who', label: cartFilters.who === 'guests' ? t('guestCustomer') : t('customersAccounts'), onRemove: () => void loadCarts({ who: '' }) }]
      : []),
  ];

  return (
    <>
      <ListToolbar>
        <FilterBar
          search={cartFilters.search}
          onSearch={(search) => void loadCarts({ search })}
          placeholder={t('searchCarts')}
          total={cartsTotal ?? undefined}
          shown={carts.length}
          chips={chips}
          onClearAll={() => void loadCarts(EMPTY_CART_FILTERS)}
        >
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <FilterField label={t('cartState')} htmlFor="cart-state">
              <Select
                id="cart-state"
                value={cartFilters.state}
                onChange={(event) => void loadCarts({ state: event.target.value as CartFilters['state'] })}
              >
                <option value="">{t('anyValue')}</option>
                <option value="open">{t('cartOpen')}</option>
                <option value="abandoned">{t('abandoned')}</option>
              </Select>
            </FilterField>
            <FilterField label={t('cartWho')} htmlFor="cart-who">
              <Select
                id="cart-who"
                value={cartFilters.who}
                onChange={(event) => void loadCarts({ who: event.target.value as CartFilters['who'] })}
              >
                <option value="">{t('anyValue')}</option>
                <option value="customers">{t('customersAccounts')}</option>
                <option value="guests">{t('guestCustomer')}</option>
              </Select>
            </FilterField>
          </div>
        </FilterBar>
      </ListToolbar>

      {error ? (
        <div className="px-4 pt-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {cartsLoading && carts.length === 0 ? (
        <TableSkeleton columns={5} />
      ) : carts.length === 0 ? (
        <EmptyState title={t('noCarts')} body={t('noCartsHint')} icon={<CartIcon />} />
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>{t('customer')}</Th>
                <Th>{t('items')}</Th>
                <Th align="end">{t('cartValue')}</Th>
                <Th>{t('status')}</Th>
                <Th align="end">{t('lastActivity')}</Th>
              </tr>
            </thead>
            <tbody>
              {carts.map((cart) => (
                <Tr key={cart.id} onClick={() => setOpen(cart)}>
                  <Td>
                    <Shopper cart={cart} />
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <div className="flex -space-x-2 rtl:space-x-reverse">
                        {cart.lines.slice(0, 3).map((line) => (
                          <Thumb key={line.variantId} src={mediaUrl(line.imageUrl)} size="xs" className="ring-2 ring-surface" />
                        ))}
                      </div>
                      <span className="min-w-0 text-sm text-ink">
                        <span className="block max-w-56 truncate">{cart.lines.map((line) => line.name).join(t('listSeparator'))}</span>
                        <span className="block text-xs text-ink-muted">{t('unitsCount', { count: count(cart.units, locale) })}</span>
                      </span>
                    </div>
                  </Td>
                  <Td align="end" data-numeric className="whitespace-nowrap font-medium text-ink">
                    {money(cart.value, locale, cart.currency)}
                  </Td>
                  <Td>
                    <CartState cart={cart} />
                  </Td>
                  <Td align="end" className="whitespace-nowrap text-ink-muted">
                    {age(cart.updatedAt, locale)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}

      <LoadMore visible={Boolean(cartsCursor)} loading={cartsLoading} onClick={() => void loadMoreCarts()} />
      <CartSheet cart={open} onClose={() => setOpen(null)} onReminded={setOpen} />
    </>
  );
}

function Shopper({ cart }: { cart: AdminCartDto }) {
  const t = useT();
  if (!cart.customer) return <Pill>{t('guestCustomer')}</Pill>;
  return (
    <>
      <Link
        to={`/customers/${cart.customer.id}`}
        onClick={(event) => event.stopPropagation()}
        className="block font-medium text-ink hover:text-brand-700 hover:underline"
      >
        {cart.customer.name || cart.customer.email}
      </Link>
      <span dir="ltr" className="block text-xs text-ink-muted">
        {cart.customer.email}
      </span>
    </>
  );
}

function CartState({ cart }: { cart: AdminCartDto }) {
  const t = useT();
  return (
    <div className="flex flex-col items-start gap-1">
      <Pill tone={cart.abandoned ? 'warning' : 'info'}>{cart.abandoned ? t('abandoned') : t('cartOpen')}</Pill>
      {cart.discountCode ? (
        // The code the shopper applied last (ADR-066).
        <span dir="ltr" data-numeric className="text-xs font-medium text-ink">
          {cart.discountCode}
        </span>
      ) : null}
      {cart.recoveryMailedAt ? <span className="text-xs text-ink-muted">{t('recoveryMailed')}</span> : null}
    </div>
  );
}

/** Once a day at most, as the API holds it (ADR-066). */
const REMIND_AGAIN_AFTER_MS = 24 * 3_600_000;

/** "I'll finish later" — the abandoned-cart message for this one shopper, by mail or text (ADR-066). */
function Reminder({ cart, onSent }: { cart: AdminCartDto; onSent: (cart: AdminCartDto) => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const mayRemind = useAuthStore((state) => can(state.user, 'support:manage'));
  const { reminding, reminderError, reminderUndelivered, remind, clearReminderError } = useShopperActivityStore();
  const [channel, setChannel] = useState<'EMAIL' | 'SMS' | 'WHATSAPP'>('EMAIL');

  useEffect(() => {
    clearReminderError();
  }, [cart.id, clearReminderError]);

  if (!cart.customer || !mayRemind) return null;
  const recent = cart.recoveryMailedAt !== null && Date.now() - new Date(cart.recoveryMailedAt).getTime() < REMIND_AGAIN_AFTER_MS;

  return (
    <section className="flex flex-col gap-3 rounded-control border border-line p-4">
      <Choices
        legend={t('sendReminder')}
        hint={t('sendReminderHint')}
        name="cart-reminder-channel"
        columns={3}
        options={[
          { value: 'EMAIL', label: t('reminderByEmail'), hint: cart.customer.email },
          { value: 'SMS', label: t('reminderBySms'), hint: t('reminderBySmsHint') },
          // The customer's WhatsApp number and marketing consent, and an approved template (ADR-106).
          { value: 'WHATSAPP', label: t('reminderByWhatsApp'), hint: t('reminderByWhatsAppHint') },
        ]}
        value={channel}
        onChange={setChannel}
      />
      {recent && cart.recoveryMailedAt ? (
        <p className="rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
          {t('remindedRecently', { when: age(cart.recoveryMailedAt, locale) })}
        </p>
      ) : null}
      {reminderError ? <Alert>{reminderError}</Alert> : null}
      {reminderUndelivered ? <Alert>{t('reminderUndelivered')}</Alert> : null}
      <div>
        <Button
          disabled={reminding || recent}
          onClick={async () => {
            const updated = await remind(cart.id, channel);
            if (updated) onSent(updated);
          }}
        >
          {reminding ? t('saving') : t('sendReminderButton')}
        </Button>
      </div>
    </section>
  );
}

/** One cart, read like the order page: the lines with their pictures, and what they come to. */
function CartSheet({
  cart,
  onClose,
  onReminded,
}: {
  cart: AdminCartDto | null;
  onClose: () => void;
  onReminded: (cart: AdminCartDto) => void;
}) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  if (!cart) return null;

  return (
    <Sheet
      open
      size="lg"
      title={cart.customer ? cart.customer.name || cart.customer.email : t('guestCart')}
      subtitle={t('cartSubtitle', { since: dateTime(cart.createdAt, locale), last: age(cart.updatedAt, locale) })}
      onClose={onClose}
    >
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-center gap-2">
          <CartState cart={cart} />
          {cart.customer ? (
            <Link to={`/customers/${cart.customer.id}`} className="text-sm font-medium text-brand-700 hover:underline">
              {t('viewCustomer')}
            </Link>
          ) : (
            <span className="text-xs text-ink-muted">{t('guestCartHint')}</span>
          )}
        </div>

        <ul className="rounded-control border border-line">
          {cart.lines.map((line) => (
            <li key={line.variantId} className="flex items-start gap-3 border-b border-line px-4 py-3 last:border-0">
              <Thumb src={mediaUrl(line.imageUrl)} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
                  <Link to={`/products/${line.productId}`} className="min-w-0 text-sm text-ink hover:text-brand-700 hover:underline">
                    {line.name}
                  </Link>
                  <span data-numeric className="whitespace-nowrap text-sm font-medium text-ink">
                    {money(line.lineTotal, locale, cart.currency)}
                  </span>
                </div>
                <div className="mt-0.5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-xs text-ink-muted">
                  <span className="min-w-0 break-words">
                    <span data-numeric dir="ltr">
                      {line.sku}
                    </span>
                    {Object.entries(line.optionLabels).length > 0
                      ? ` · ${Object.entries(line.optionLabels)
                          .map(([name, value]) => `${name}: ${value}`)
                          .join(', ')}`
                      : ''}
                  </span>
                  <span data-numeric className="whitespace-nowrap">
                    {count(line.quantity, locale)} × {money(line.unitPrice, locale, cart.currency)}
                  </span>
                </div>
                {!line.available ? (
                  <p className="mt-1 text-xs text-danger">{t('lineNotOnSale')}</p>
                ) : line.stock < line.quantity ? (
                  <p className="mt-1 text-xs text-warning">{t('lineShortOfStock', { stock: count(line.stock, locale) })}</p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>

        <dl className="flex flex-col gap-2 text-sm">
          <AmountRow label={t('cartValue')} value={money(cart.value, locale, cart.currency)} emphasis />
          <p className="text-xs text-ink-muted">{t('cartValueHint')}</p>
        </dl>

        {cart.discountCode ? (
          <p className="rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink">
            {t('cartCouponApplied')}{' '}
            <span dir="ltr" data-numeric className="font-medium">
              {cart.discountCode}
            </span>
          </p>
        ) : null}

        <Reminder cart={cart} onSent={onReminded} />

        {cart.recoveryMailedAt ? (
          <p className="rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
            {t('recoveryMailedOn', { date: dateTime(cart.recoveryMailedAt, locale) })}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}

/** One kind of saved list, counted by product. */
function SavedProducts({ kind }: { kind: SavedListKind }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { saved, error, loadSaved, loadMoreSaved, openSavers } = useShopperActivityStore();
  const list = saved[kind];
  const [open, setOpen] = useState<SavedProductSummaryDto | null>(null);

  useEffect(() => {
    void loadSaved(kind);
  }, [kind, loadSaved, locale]);

  return (
    <>
      <ListToolbar>
        <FilterBar
          search={list.search}
          onSearch={(search) => void loadSaved(kind, search)}
          placeholder={t('searchSkuOrName')}
          total={list.total ?? undefined}
          shown={list.items.length}
        />
      </ListToolbar>

      {error ? (
        <div className="px-4 pt-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      {list.loading && list.items.length === 0 ? (
        <TableSkeleton columns={5} />
      ) : list.items.length === 0 ? (
        <EmptyState
          title={kind === 'WISHLIST' ? t('noWishlists') : t('noCompareLists')}
          body={t('savedListsHint')}
          icon={<StarIcon />}
        />
      ) : (
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <Th>{t('product')}</Th>
                <Th align="end">{t('savedBy')}</Th>
                <Th align="end">{t('priceToday')}</Th>
                <Th align="end">{t('priceDropFor')}</Th>
                <Th align="end">{t('lastSaved')}</Th>
              </tr>
            </thead>
            <tbody>
              {list.items.map((product) => (
                <Tr
                  key={product.productId}
                  onClick={() => {
                    setOpen(product);
                    void openSavers(product.productId, kind);
                  }}
                >
                  <Td>
                    <div className="flex items-center gap-3">
                      <Thumb src={mediaUrl(product.imageUrl)} size="sm" />
                      <span className="font-medium text-ink">{product.name}</span>
                    </div>
                  </Td>
                  <Td align="end">
                    <span data-numeric className="block font-medium text-ink">
                      {count(product.saves, locale)}
                    </span>
                    <span className="block text-xs text-ink-muted">
                      {t('accountsAndGuests', { accounts: count(product.customers, locale), guests: count(product.guests, locale) })}
                    </span>
                  </Td>
                  <Td align="end" data-numeric className="whitespace-nowrap text-ink">
                    {money(product.price, locale, product.currency)}
                  </Td>
                  <Td align="end" data-numeric className="text-ink-muted">
                    {product.priceDrops > 0 ? <Pill tone="success">{count(product.priceDrops, locale)}</Pill> : '—'}
                  </Td>
                  <Td align="end" className="whitespace-nowrap text-ink-muted">
                    {age(product.lastSavedAt, locale)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </TableScroll>
      )}

      <LoadMore visible={Boolean(list.cursor)} loading={list.loading} onClick={() => void loadMoreSaved(kind)} />
      <SaversSheet product={open} kind={kind} onClose={() => setOpen(null)} />
    </>
  );
}

/** The accounts that saved one product; guests counted beside them. */
function SaversSheet({ product, kind, onClose }: { product: SavedProductSummaryDto | null; kind: SavedListKind; onClose: () => void }) {
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const { savers, loadMoreSavers, closeSavers } = useShopperActivityStore();
  if (!product) return null;

  const close = () => {
    closeSavers();
    onClose();
  };

  return (
    <Sheet
      open
      title={product.name}
      subtitle={kind === 'WISHLIST' ? t('saversWishlist') : t('saversCompare')}
      onClose={close}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Thumb src={mediaUrl(product.imageUrl)} size="md" />
          <dl className="grid flex-1 grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-ink-muted">{t('savedBy')}</dt>
            <dd data-numeric className="text-ink">
              {t('accountsAndGuests', { accounts: count(product.customers, locale), guests: count(product.guests, locale) })}
            </dd>
            <dt className="text-ink-muted">{t('priceToday')}</dt>
            <dd data-numeric className="text-ink">
              {money(product.price, locale, product.currency)}
            </dd>
          </dl>
        </div>

        {product.guests > 0 ? (
          <p className="rounded-control bg-surface-sunken px-3 py-2 text-sm text-ink-muted">
            {t('guestsCountedNotNamed', { count: count(product.guests, locale) })}
          </p>
        ) : null}

        {savers?.loading && savers.items.length === 0 ? (
          <TableSkeleton columns={2} rows={3} />
        ) : (
          <ul className="rounded-control border border-line">
            {(savers?.items ?? []).map((saver) => (
              <li
                key={saver.customer.id}
                className="flex items-center justify-between gap-3 border-b border-line px-4 py-3 text-sm last:border-0"
              >
                <span className="min-w-0">
                  <Link to={`/customers/${saver.customer.id}`} className="block font-medium text-ink hover:text-brand-700 hover:underline">
                    {saver.customer.name || saver.customer.email}
                  </Link>
                  <span dir="ltr" className="block truncate text-xs text-ink-muted">
                    {saver.customer.email}
                  </span>
                </span>
                <span className="shrink-0 text-end text-xs text-ink-muted">
                  <span className="block">{date(saver.savedAt, locale)}</span>
                  {saver.priceAtSave !== null && saver.priceAtSave > product.price ? (
                    <span data-numeric className="block text-success">
                      {t('savedAtPrice', { amount: money(saver.priceAtSave, locale, product.currency) })}
                    </span>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        )}

        <LoadMore visible={Boolean(savers?.cursor)} loading={savers?.loading ?? false} onClick={() => void loadMoreSavers()} />
      </div>
    </Sheet>
  );
}
